import { seedProducts } from '../../data/seedProducts';
import type { ProductInput } from '../../types';
import type { Product } from '../../types';
import { normalizeImages, slugify, MAX_PRODUCT_IMAGES, validateProductImageFiles } from '../../utils/product';
import { delay, mockStorageKeys, readJson, writeJson } from '../mock/storage';

type LegacyProduct = Product & { image?: string; images?: string[] };

function migrateProduct(product: LegacyProduct): Product {
  const stock =
    typeof product.stock === 'number'
      ? Math.max(0, Math.floor(product.stock))
      : product.inStock
        ? 10
        : 0;

  let images = product.images;
  if (!images || images.length === 0) {
    images = product.image ? [product.image] : [];
  }

  const { image: _, ...rest } = product;

  return {
    ...rest,
    images: normalizeImages(images),
    stock,
    inStock: stock > 0,
  };
}

function ensureProductsSeeded(): Product[] {
  const existing = readJson<LegacyProduct[] | null>(mockStorageKeys.products, null);

  if (existing && existing.length > 0) {
    const migrated = existing.map(migrateProduct);
    const needsSave = existing.some(
      (p) => !p.images?.length || typeof p.stock !== 'number' || 'image' in p,
    );
    if (needsSave) writeJson(mockStorageKeys.products, migrated);
    return migrated;
  }

  writeJson(mockStorageKeys.products, seedProducts);
  return seedProducts;
}

function saveProducts(products: Product[]) {
  writeJson(mockStorageKeys.products, products);
}

function normalizeProduct(input: ProductInput, existingId?: string): Product {
  const id = input.id?.trim() || existingId || slugify(input.name) || crypto.randomUUID();
  const stock = Math.max(0, Math.floor(Number(input.stock) || 0));
  const images = normalizeImages(input.images);

  return {
    id,
    name: input.name.trim(),
    description: input.description.trim(),
    price: Number(input.price) || 0,
    currency: input.currency,
    priceLabel: input.priceLabel?.trim() || undefined,
    category: input.category.trim(),
    images,
    benefits: input.benefits.filter(Boolean),
    stock,
    inStock: stock > 0,
    specs: input.specs && Object.keys(input.specs).length > 0 ? input.specs : undefined,
  };
}

export async function mockFetchProducts(): Promise<Product[]> {
  await delay(200);
  return ensureProductsSeeded();
}

export async function mockGetProduct(id: string): Promise<Product> {
  await delay(200);
  const product = ensureProductsSeeded().find((item) => item.id === id);
  if (!product) {
    throw new Error('Ürün bulunamadı.');
  }
  return product;
}

export async function mockCreateProduct(input: ProductInput): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const product = normalizeProduct(input);

  if (products.some((p) => p.id === product.id)) {
    throw new Error('Bu ürün kimliği zaten kullanılıyor.');
  }

  const next = [...products, product];
  saveProducts(next);
  return product;
}

export async function mockUpdateProduct(id: string, input: ProductInput): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((p) => p.id === id);

  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const product = normalizeProduct(input, id);
  const next = [...products];
  next[index] = product;
  saveProducts(next);
  return product;
}

export async function mockDeleteProduct(id: string): Promise<void> {
  await delay();

  const products = ensureProductsSeeded();
  const next = products.filter((p) => p.id !== id);

  if (next.length === products.length) {
    throw new Error('Ürün bulunamadı.');
  }

  saveProducts(next);
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`${file.name} okunamadı.`));
    reader.readAsDataURL(file);
  });
}

export async function mockUploadProductImages(id: string, files: File[]): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const currentImages = products[index].images;
  const validationError = validateProductImageFiles(files, currentImages.length);
  if (validationError) {
    throw new Error(validationError);
  }

  if (currentImages.length + files.length > MAX_PRODUCT_IMAGES) {
    throw new Error(`Ürün başına en fazla ${MAX_PRODUCT_IMAGES} görsel yüklenebilir.`);
  }

  const uploaded = await Promise.all(files.map((file) => fileToDataUrl(file)));
  const nextProduct = {
    ...products[index],
    images: [...currentImages, ...uploaded],
  };

  const next = [...products];
  next[index] = nextProduct;
  saveProducts(next);
  return nextProduct;
}

export async function mockDeleteProductImage(id: string, url: string): Promise<Product> {
  await delay();

  const products = ensureProductsSeeded();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) {
    throw new Error('Ürün bulunamadı.');
  }

  const nextImages = products[index].images.filter((image) => image !== url);
  if (nextImages.length === products[index].images.length) {
    throw new Error('Görsel bulunamadı.');
  }

  const nextProduct = {
    ...products[index],
    images: nextImages,
  };

  const next = [...products];
  next[index] = nextProduct;
  saveProducts(next);
  return nextProduct;
}
